import axios from 'axios';
import Link from 'next/link';
import Router from 'next/router';
import * as gtag from '../utils/gtag.js';
import { apiUrl } from '../utils/api.js';
import { HrBrick } from './HrBrick.jsx';
import { MoneyInput } from './MoneyInput.jsx';
import { ButtonLink } from './ButtonLink.jsx';
import { SubmitButton } from './SubmitButton.jsx';
import { useState, useEffect, useRef } from 'react';
import { CheckboxInput } from './CheckboxInput.jsx';
import { PostalCodeInput } from './PostalCodeInput.jsx';
import { formatNumber } from '../utils/format-number.js';
import { BankSelector } from './BankSelector.jsx';
import { useStickyState } from '../hooks/sticky-state.js';
import { InterestRateInput } from './InterestRateInput.jsx';
import { AssociationSelector } from './AssociationSelector.jsx';
import { BoliglanIllustrationPartial } from './BoliglanIllustrationPartial.jsx';

function scrollToAndFocusElement(element) {
  const isSmoothScrollSupported =
    'scrollBehavior' in document.documentElement.style;

  if (isSmoothScrollSupported) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else {
    element.scrollIntoView(false);
  }

  element.focus();
}

function TheForm({ onSubmit, isHidden, view, defaultInterestRate = null }) {
  const [canSubmit, setCanSubmit] = useState(false);
  const [bank, setBank] = useState(null);
  const [postalCode, setPostalCode] = useState(null);
  const [loanAmount, setLoanAmount] = useState(null);
  const [propertyValue, setPropertyValue] = useState(null);
  const [interestRate, setInterestRate] = useState(defaultInterestRate);
  const [isAssociationMember, setIsAssociationMember] = useState(false);
  const [association, setAssociation] = useState(null);
  const [recentlyCreatedLoanEntry] = useStickyState(
    'recently-created-loan-entry-v2',
    null
  );

  const postalCodeInputElement = useRef();
  const submitButtonElement = useRef();

  useEffect(
    function syncCanSubmit() {
      const value = Boolean(
        bank && propertyValue && postalCode && loanAmount && interestRate
      );

      if (canSubmit !== value) {
        setCanSubmit(value);
      }
    },
    [bank, propertyValue, postalCode, loanAmount, interestRate]
  );

  useEffect(() => {
    if (!isAssociationMember) {
      setAssociation(null);
    }
  }, [isAssociationMember]);

  function handleSubmit(event) {
    event.preventDefault();

    onSubmit({
      bank,
      propertyValue,
      postalCode,
      loanAmount,
      interestRate,
      isAssociationMember,
      association,
    });

    if (view === 'standalone') {
      window.scrollTo(0, 0);
    }

    gtag.event({
      category: 'Boliglånskjema',
      action: 'Klikk på "Gå videre"',
    });
  }

  return (
    <>
      <div className="form__headline">
        {view === 'standalone' ? (
          <h1>Ta testen og spar penger</h1>
        ) : (
          <h2>Ta testen og spar penger</h2>
        )}
        <HrBrick />
      </div>
      <form
        className={`form ${isHidden ? 'form--is-hidden' : ''}`}
        onSubmit={handleSubmit}
      >
        <div className="form__fields">
          {recentlyCreatedLoanEntry && (
            <p className="recentlyCreatedLoanEntryNotice">
              Psst! Du har allerede en boliglåns&shy;rapport hos oss:
              <span>
                <ButtonLink
                  href="/boliglansrapport/[id]"
                  as={`/boliglansrapport/${recentlyCreatedLoanEntry}`}
                >
                  Gå til boliglånsrapport
                </ButtonLink>
              </span>
            </p>
          )}
          {bank ? (
            <div className="selectedBank">
              <div className="form__label">Din bank:</div>
              <div className="selectedBank__container">
                <div className="selectedBank__bank">{bank.name}</div>
                <button
                  type="button"
                  className="selectedBank__clear"
                  onClick={() => setBank(null)}
                >
                  Endre
                </button>
              </div>
            </div>
          ) : (
            <BankSelector
              label="I hvilken bank har du boliglån?"
              onChange={bank => {
                setBank(bank);

                if (postalCodeInputElement.current.scrollIntoView) {
                  setTimeout(() =>
                    scrollToAndFocusElement(postalCodeInputElement.current)
                  );
                }
              }}
            />
          )}
          <PostalCodeInput
            label="Hva er postnummeret ditt?"
            onChange={setPostalCode}
            ref2={postalCodeInputElement}
          />
          <InterestRateInput
            onChange={setInterestRate}
            label="Hva er den flytende, effektive renten på lånet?"
            defaultValue={defaultInterestRate}
          />
          <MoneyInput
            label="Hvor mye er lånet på i dag?"
            onChange={setLoanAmount}
          />
          {loanAmount >= 100000 && (
            <MoneyInput
              label="Hva er boligens verdi?"
              onChange={setPropertyValue}
            />
          )}
          <CheckboxInput
            label="Er du medlem av en forening?"
            text="Ja, jeg er medlem av en forening som gir meg en gunstigere rente på lånet jeg har!"
            onChange={setIsAssociationMember}
          />
          {isAssociationMember && (
            <>
              {association ? (
                <div className="selectedBank">
                  <div className="form__label">Forening:</div>
                  <div className="selectedBank__container">
                    <div className="selectedBank__bank">{association.name}</div>
                    <button
                      type="button"
                      className="selectedBank__clear"
                      onClick={() => setAssociation(null)}
                    >
                      Endre
                    </button>
                  </div>
                </div>
              ) : (
                <AssociationSelector
                  label="Hvilken forening er du medlem av?"
                  onChange={association => {
                    setAssociation(association);

                    if (submitButtonElement.current.scrollIntoView) {
                      setTimeout(() =>
                        scrollToAndFocusElement(submitButtonElement.current)
                      );
                    }
                  }}
                />
              )}
            </>
          )}
          <div className="submitContainer">
            <SubmitButton
              type="submit"
              disabled={!canSubmit}
              ref2={submitButtonElement}
            >
              Gå videre
            </SubmitButton>
          </div>
        </div>
        {view === 'standalone' && (
          <div className="illustration">
            <BoliglanIllustrationPartial />
          </div>
        )}
      </form>
      <style jsx>{`
        .form__headline h1,
        .form__headline h2 {
          font-weight: 800;
          font-size: 56px;
          color: var(--bgBlueish__text);
          margin: 32px 0 9px 0;
          line-height: 0.9;
        }

        .form__headline h2 {
          font-size: 38px;
          margin-top: 0;
        }

        .form {
          margin-top: 36px;
        }

        .form--is-hidden {
          position: absolute;
          visibility: hidden;
        }

        .form__label {
          margin-bottom: 2px;
          font-weight: 600;
          color: var(--bgBlueish__text);
        }

        .selectedBank {
          margin-bottom: 18px;
        }

        .selectedBank__container {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .selectedBank__bank {
          font-weight: 600;
          font-size: 18px;
          color: var(--bgBlueish__text);
        }

        .selectedBank__clear {
          font-weight: 500;
          padding: 4px 6px;
          font-size: 14px;
          border-radius: 2px;
          background-color: var(--bgBlueish);
          color: var(--bgBlueish__textDim);
        }

        .submitContainer {
          text-align: right;
        }

        .illustration {
          display: none;
        }

        .recentlyCreatedLoanEntryNotice {
          margin: -18px 0 18px 0;
          text-align: right;
          font-weight: 500;
          color: var(--bgBlueish__text);
        }

        .recentlyCreatedLoanEntryNotice :global(a) {
          margin-top: 9px;
        }

        .recentlyCreatedLoanEntryNotice > span {
          display: block;
        }

        @media screen and (min-width: 750px) {
          .form__headline h1 {
            max-width: 380px;
            margin: ${view === 'inline' ? '0' : '54px'} 0 27px 0;
          }

          .form__fields {
            min-width: 420px;
            width: 420px;
          }
        }

        @media screen and (min-width: 980px) {
          .illustration {
            position: relative;
            display: block;
            margin-top: -180px;
            margin-left: 80px;
          }

          .illustration::after {
            content: '';
            position: absolute;
            top: 200px;
            right: 0;
            display: block;
            width: 480px;
            height: 480px;
            border-radius: 50%;
            background-color: #f3f5ff;
            z-index: -1;
          }

          .illustration :global(svg) {
            max-width: 100%;
            height: auto;
          }

          .form {
            display: flex;
            justify-content: space-between;
          }
        }
      `}</style>
    </>
  );
}

function SummaryScreen({
  bank,
  propertyValue,
  postalCode,
  loanAmount,
  interestRate,
  isAssociationMember,
  association,
  onGoingBack,
  onSubmit,
  isSubmitting,
  submissionError,
}) {
  function handleSubmit() {
    onSubmit({
      bank: bank.id,
      propertyValue,
      postalCode: postalCode.id,
      loanAmount,
      interestRate,
      isAssociationMember,
      association: association && association.id,
    });

    gtag.event({
      category: 'Boliglånskjema',
      action: 'Klikk på send inn-knappen',
    });
  }

  return (
    <div className="root">
      <h1>Stemmer tallene?</h1>
      <dl className="list">
        <div className="list__line">
          <dt>Bank:</dt>
          <dd>{bank.name}</dd>
        </div>
        <div className="list__line">
          <dt>Postnummer:</dt>
          <dd>
            {postalCode.id} {postalCode.city}
          </dd>
        </div>
        <div className="list__line">
          <dt>Effektiv rente:</dt>
          <dd>{(interestRate / 100).toFixed(2).replace('.', ',')} %</dd>
        </div>
        <div className="list__line">
          <dt>Er renten flytende?</dt>
          <dd>Ja!</dd>
        </div>
        <div className="list__line">
          <dt>Lånebeløp:</dt>
          <dd>{formatNumber(loanAmount, null, 'kr')}</dd>
        </div>
        <div className="list__line">
          <dt>Boligens verdi:</dt>
          <dd>{formatNumber(propertyValue, null, 'kr')}</dd>
        </div>
        <div className="list__line">
          <dt>Medlem av forening?</dt>
          <dd>
            {isAssociationMember ? (
              <>
                Ja!
                {association && <> {association.name}</>}
              </>
            ) : (
              'Nei'
            )}
          </dd>
        </div>
      </dl>
      {submissionError && (
        <div className="submissionError">{submissionError.message}</div>
      )}
      <div className="buttons">
        {!isSubmitting && (
          <SubmitButton type="button" level="secondary" onClick={onGoingBack}>
            Gå tilbake
          </SubmitButton>
        )}
        <SubmitButton
          type="button"
          disabled={isSubmitting}
          onClick={handleSubmit}
          isLoading={isSubmitting}
          loadingLabel="Henter din rapport..."
        >
          Ja! Vis sparemuligheter
        </SubmitButton>
      </div>
      <div className="privacyText">
        <p>
          Når du går videre, godtar du at vi lagrer og bruker opplysningene du
          har oppgitt til å danne et bedre sammenlignings&shy;grunnlag i
          tjenesten.
        </p>
        <p>
          Det er derfor viktig at du passer på å fylle inn så nøyaktige
          opplysninger som mulig, da dette vil gi deg et mer nøyaktig estimat på
          hvor mye du vil spare.
        </p>
        <p>
          Les mer om databehandling i{' '}
          <Link href="/personvern">
            <a target="_blank">vår personvernerklæring</a>
          </Link>
          .
        </p>
      </div>
      <style jsx>{`
        .root {
          border-radius: 2px;
          background-color: var(--bgBlueish);
          color: var(--bgBlueish__text);
        }

        .list__line {
          display: flex;
          justify-content: space-between;
          margin: 14px 0;
          padding-bottom: 4px;
          border-bottom: 2px dashed rgba(0, 0, 0, 0.06);
        }

        .list {
          font-size: 16px;
          color: var(--bgBlueish__text);
        }

        .list dt {
          font-weight: 700;
        }

        .list dd {
          font-weight: 500;
        }

        .buttons {
          margin: 36px 0;
          display: flex;
          justify-content: flex-end;
        }

        .buttons :global(button:last-child) {
          margin-left: 18px;
        }

        .privacyText {
          margin-top: 36px;
          font-size: 14px;
          color: var(--bgBlueish__textDim);
          font-weight: 500;
          line-height: 1.5;
        }

        .privacyText a {
          color: currentColor;
        }

        .privacyText a:hover {
          color: var(--bgBlueish__text);
        }

        .submissionError {
          margin-top: 18px;
          margin-bottom: -18px;
          color: #fb1919;
          font-weight: 500;
          line-height: 1.5;
        }

        .submissionError p {
          margin: 0;
        }

        .submissionError p + p {
          margin-top: 18px;
        }

        @media screen and (min-width: 750px) {
          .root {
            max-width: 420px;
          }
        }
      `}</style>
    </div>
  );
}

function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function getUserOrigin() {
  if (!window.localStorage) {
    return null;
  }

  const payload = window.localStorage.getItem('pengerno-user-origin');

  return payload ? JSON.parse(payload) : null;
}

export function LoanReportForm({
  defaultInterestRate = null,
  view = 'standalone',
}) {
  const [data, setData] = useState(null);
  const [isSummaryScreenVisible, setIsSummaryScreenVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [
    recentlyCreatedLoanEntry,
    setRecentlyCreatedLoanEntry,
  ] = useStickyState('recently-created-loan-entry-v2', null);

  useEffect(() => {
    setSubmissionError(null);
  }, [isSummaryScreenVisible]);

  async function handleSubmit(data) {
    setIsSubmitting(true);

    try {
      const responsePromise = axios.post(apiUrl('loan-entries'), {
        ...data,
        recentlyCreatedLoanEntry,
        userOrigin: getUserOrigin(),
      });

      const [response] = await Promise.all([responsePromise, sleep(2000)]);

      setRecentlyCreatedLoanEntry(response.data.id);

      Router.push('/boliglansrapport/[id]', response.data.loanReportUrl).then(
        () => {
          window.scrollTo(0, 0);
        }
      );
    } catch (error) {
      if (!error.isAxiosError) {
        throw error;
      }

      gtag.event({
        category: 'Boliglånskjema',
        action: 'Innsendingsfeil',
        label: JSON.stringify(error.response.data),
      });

      await sleep(3000);

      setIsSubmitting(false);

      setSubmissionError({
        message: (
          <>
            <p>Det er en feil som gjør at opplysningene ikke kan behandles.</p>
            <p>
              Mest sannsynlig er det en eller flere verdier som ikke er gyldige.
              Prøv igjen eller gå tilbake for å endre på opplysningene.
            </p>
          </>
        ),
      });
    }
  }

  return (
    <>
      <TheForm
        onSubmit={data => {
          setData(data);

          setIsSummaryScreenVisible(true);
        }}
        isHidden={isSummaryScreenVisible}
        view={view}
        defaultInterestRate={defaultInterestRate}
      />
      {isSummaryScreenVisible && (
        <SummaryScreen
          isSubmitting={isSubmitting}
          submissionError={submissionError}
          onGoingBack={() => {
            setIsSummaryScreenVisible(false);

            gtag.event({
              category: 'Boliglånskjema',
              action: 'Klikk på "Gå tilbake"',
            });
          }}
          onSubmit={handleSubmit}
          {...data}
        />
      )}
    </>
  );
}
